import { z } from 'zod/v4';
import { ProjectBodyV1Schema, SlotMapV1Schema } from './project-body.js';

// @intent: Template registry contracts for schema-safe template cloning and slot variant workflows (F6)
// @flow: Runtime/template services validate template manifests and clone payloads before project creation
// @stale: Expand with TemplateManifestV2 once persisted manifests migrate

export const TemplateSlotKindV1Schema = z.enum(['asset', 'text', 'color', 'url']);
export type TemplateSlotKindV1 = z.infer<typeof TemplateSlotKindV1Schema>;

export const TemplateSlotIntentV1Schema = z.enum(['sprite', 'icon', 'ui', 'scene', 'background']);
export type TemplateSlotIntentV1 = z.infer<typeof TemplateSlotIntentV1Schema>;

export const TemplateSlotSpecV1Schema = z.strictObject({
  name: z.string().min(1),
  kind: TemplateSlotKindV1Schema,
  required: z.boolean(),
  defaultValue: z.unknown().optional(),
  intent: TemplateSlotIntentV1Schema.optional(),
  promptTemplate: z.string().min(1).optional(),
  description: z.string().min(1),
});
export type TemplateSlotSpecV1 = z.infer<typeof TemplateSlotSpecV1Schema>;

export const TemplateManifestV1Schema = z.strictObject({
  version: z.literal('v1'),
  category: z.string().min(1),
  thumbnailUrl: z.string().trim().min(1),
  baseProjectBody: ProjectBodyV1Schema,
  requiredSlots: z.array(TemplateSlotSpecV1Schema),
  targetCapabilities: z.array(z.string().trim().min(1)),
  metadata: z.record(z.string(), z.unknown()),
});
export type TemplateManifestV1 = z.infer<typeof TemplateManifestV1Schema>;

export const TemplateListItemV1Schema = z.strictObject({
  id: z.uuid(),
  slug: z.string().min(1),
  name: z.string().min(1),
  category: z.string().min(1),
  thumbnailUrl: z.string().trim().min(1),
  requiredSlots: z.array(TemplateSlotSpecV1Schema),
  targetCapabilities: z.array(z.string().trim().min(1)),
  metadata: z.record(z.string(), z.unknown()),
});
export type TemplateListItemV1 = z.infer<typeof TemplateListItemV1Schema>;

export const TemplateListResponseSchema = z.strictObject({
  templates: z.array(TemplateListItemV1Schema),
});
export type TemplateListResponse = z.infer<typeof TemplateListResponseSchema>;

export const CloneTemplateRequestSchema = z.strictObject({
  name: z.string().trim().min(1).optional(),
});
export type CloneTemplateRequest = z.infer<typeof CloneTemplateRequestSchema>;

export const CloneTemplateResponseSchema = z.strictObject({
  projectId: z.uuid(),
  latestCommitId: z.uuid(),
  templateId: z.uuid(),
  templateSlug: z.string().trim().min(1),
});
export type CloneTemplateResponse = z.infer<typeof CloneTemplateResponseSchema>;

export const SlotVariantV1Schema = z.strictObject({
  id: z.uuid(),
  name: z.string().min(1),
  slotMap: SlotMapV1Schema,
  createdAt: z.iso.datetime(),
});
export type SlotVariantV1 = z.infer<typeof SlotVariantV1Schema>;
